import { reportBrokenInterpolation, t, type I18nLocale } from '@ardoq/i18n';
import i18n from 'i18next';

type ArdoqErrorArgs = {
  error: Error;
  userMessage?: string;
  traceId?: string;
  isAborted?: boolean;
  errorCode?: string;
  errorDetails?: Record<string, unknown>;
};

/**
 * Represents the error case of Result<T>
 * It should never be instantiated or exported by itself.
 */
class ArdoqError {
  error: Error;
  userMessage?: string;
  traceId?: string;
  isAborted?: boolean;
  errorCode?: string;
  errorDetails?: Record<string, unknown>;

  constructor({
    error,
    userMessage,
    traceId,
    isAborted,
    errorCode,
    errorDetails,
  }: ArdoqErrorArgs) {
    this.error = error;
    this.userMessage = userMessage;
    this.traceId = traceId;
    this.isAborted = isAborted;
    this.errorCode = errorCode;
    this.errorDetails = errorDetails;
  }
}
export type { ArdoqError };

export type Result<T> = T | ArdoqError;

export const isArdoqError = (
  possibleError: unknown
): possibleError is ArdoqError => possibleError instanceof ArdoqError;

export const isSuccessResponse = <T>(x: Result<T>): x is T => !isArdoqError(x);

export const toArdoqError = (args: ArdoqErrorArgs): ArdoqError =>
  new ArdoqError(args);

/**
 * We first try to return the userMessage provided by the API.
 * Secondly we try to return the provided fallbackmessage
 * because this is what we expect that the error would be.
 * And finally we return the error message as a last resort to get some information.
 * */
export const getArdoqErrorMessage = (
  error: ArdoqError,
  fallbackMessage?: string
): string => {
  return error.userMessage ?? fallbackMessage ?? error.error.message;
};

const UNRESOLVED_PLACEHOLDER_PATTERN = /\{\{[^}]+\}\}/;

/**
 * Translates an ArdoqError using its `errorCode` as the i18next key in the
 * `apiErrors` namespace, with the English `userMessage` as the fallback. When
 * `errorCode` is missing, delegates entirely to `getArdoqErrorMessage`.
 *
 * Interpolation: `errorDetails` from the backend is passed to `t()` so
 * translated templates with `{{placeholder}}` tokens render correctly. If a
 * template still contains unresolved `{{...}}` after substitution
 * (placeholder-name drift between BE and FE), falls back to the BE's
 * already-interpolated English `userMessage` and reports the mismatch.
 */
export const translateApiError = (
  error: ArdoqError,
  i18nLocale: I18nLocale,
  fallbackMessage?: string
): string => {
  const englishMessage = getArdoqErrorMessage(error, fallbackMessage);
  if (!error.errorCode) return englishMessage;
  const hasDetails = error.errorDetails !== undefined;
  const details = error.errorDetails ?? {};
  const translated = t(
    i18nLocale,
    `apiErrors:${error.errorCode}`,
    details,
    englishMessage
  );
  if (UNRESOLVED_PLACEHOLDER_PATTERN.test(translated)) {
    reportBrokenInterpolation({
      locale: i18n.language,
      code: error.errorCode,
      detailKeys: Object.keys(details),
      cause: hasDetails ? 'placeholder-mismatch' : 'missing-details',
    });
    return englishMessage;
  }
  return translated;
};

/**
 * Get a possibly localized `errorMessage` (via `translateApiError`) together
 * with a `supportMessage`, a localized tip to contact Support including the traceId information, if available.
 */
export const translateApiErrorWithTraceId = (
  error: ArdoqError,
  i18nLocale: I18nLocale,
  fallbackMessage?: string
): { userMessage: string; supportMessage?: string } => {
  const userMessage = translateApiError(error, i18nLocale, fallbackMessage);
  const { traceId } = error;
  if (!traceId) return { userMessage };
  const englishFallback = `If the issue persists, contact Support and include this trace ID: ${traceId}`;
  const supportMessage = t(
    i18nLocale,
    'commonHelpers:errorWithTraceId',
    { traceId },
    englishFallback
  );
  return { userMessage, supportMessage };
};

/**
 * Get the source error of the ArdoqError.
 * NOTE: Should not be used in application code, Sentry understands ArdoqError
 * so you do not have to unwrap this.
 */
export const getErrorForLogging = (error: ArdoqError): Error => {
  return error.error;
};

// TODO[ARD-22935]: Make return null
export const getArdoqErrorTraceId = (error: ArdoqError): string => {
  return error.traceId ?? '';
};

/**
 * @deprecated Batch events should be handled by the backend. The need to use this function might
 * be a sign that the backend should be updated.
 */
export const resultsToPayloadOrError = <Payload>(
  results: Result<Payload>[],
  message?: string
): Result<Payload[]> => {
  const ardoqErrors = results.filter(isArdoqError);
  if (ardoqErrors.length) {
    const errors = ardoqErrors.map(getErrorForLogging);
    const error = new AggregateError(
      errors,
      message ?? getArdoqErrorMessage(ardoqErrors[0])
    );
    return toArdoqError({
      error,
    });
  }
  return results as Payload[];
};
